import { createContext, useContext, useMemo, useState, useCallback, useEffect, useRef, memo, useLayoutEffect, forwardRef } from 'react';
import cn from 'classnames';
import { createBemBlockBuilder } from '@ebsco-ui/utils';
import { jsx, jsxs } from 'react/jsx-runtime';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons/faAngleDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

var SectionNavigationContext = createContext({});

// TODO: use new asserts syntax when Typescript will be updated
var isContextPresent = function isContextPresent(context) {
  return Object.keys(context).length !== 0;
};

var useSectionNavigationContext = function useSectionNavigationContext() {
  var context = useContext(SectionNavigationContext);

  if (!isContextPresent(context)) {
    throw new Error("SectionNavigation compound components cannot be rendered outside the SectionNavigation component");
  }

  return context;
};

var SectionNavigationContextComponent = function SectionNavigationContextComponent(_ref) {
  var children = _ref.children;
  var context = useSectionNavigationContext();
  return children(context);
};

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) {
      arr2[i] = arr[i];
    }

    return arr2;
  }
}

function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}

var _excluded$9 = ["registeredLists", "openedLists", "toggleOpenList", "getBlocksWith", "toggleCollapsedLabel", "toggleExpandedLabel", "uniqueName", "index", "id", "onClick", "onKeyDown", "className"];

function ownKeys$a(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$a(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$a(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$a(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var spreadableProp$1 = function spreadableProp(prop) {
  return Array.isArray(prop) ? prop : [prop];
};
/**
 * buildGetToggleProps
 *
 * @param {string} id
 * @param {number | string} index
 * @param {Function} onClick
 * @param {Function} onKeyDown
 * @param {string | string[]} className
 */


var buildGetToggleProps = function buildGetToggleProps(_ref) {
  var registeredLists = _ref.registeredLists,
      openedLists = _ref.openedLists,
      toggleOpenList = _ref.toggleOpenList,
      getBlocksWith = _ref.getBlocksWith,
      toggleCollapsedLabel = _ref.toggleCollapsedLabel,
      toggleExpandedLabel = _ref.toggleExpandedLabel,
      uniqueName = _ref.uniqueName,
      index = _ref.index,
      itemId = _ref.id,
      _ref$onClick = _ref.onClick,
      consumerOnClick = _ref$onClick === void 0 ? function () {} : _ref$onClick,
      _ref$onKeyDown = _ref.onKeyDown,
      consumerOnKeyDown = _ref$onKeyDown === void 0 ? function () {} : _ref$onKeyDown,
      consumerClassName = _ref.className,
      consumerProps = _objectWithoutProperties(_ref, _excluded$9);

  /**
   * getParentContainerByIndex
   *
   * The index lookup needs to happen after the parent container has been registered.
   * The parent containers are registered inside of a ref callback which has its own lifecycle.
   * Offloading the lookup to a callback should ensure the component has had
   * enough time to initialize and the desired container is available.
   *
   * @param {string} containerIndex
   * @returns { index: number | string; ref: ReactNode }
   */
  var getParentContainerByIndex = function getParentContainerByIndex(containerIndex) {
    var listContainer = registeredLists.current.find(function (_ref2) {
      var listIndex = _ref2.index;
      return listIndex === containerIndex;
    });

    if (!listContainer) {
      return {};
    }

    return listContainer;
  };

  var isOpen = openedLists.indexOf(index) !== -1;
  return _objectSpread$a({
    onClick: function onClick(event) {
      event.persist();
      toggleOpenList(index);
      consumerOnClick({
        event: event,
        containerProps: _objectSpread$a({}, getParentContainerByIndex(index))
      });
    },
    onKeyDown: function onKeyDown(event) {
      var key = event.key;
      event.persist();
      var keyHandled = false;

      switch (key) {
        case " ":
        case "Spacebar":
        case "Enter":
          keyHandled = true;
          break;

        case "Esc":
        case "Escape":
          if (isOpen) {
            keyHandled = true;
          }

      }

      if (keyHandled) {
        event.preventDefault();
        toggleOpenList(index);
      }

      consumerOnKeyDown({
        event: event,
        containerProps: _objectSpread$a({}, getParentContainerByIndex(index))
      });
    },
    className: cn.apply(void 0, [getBlocksWith("__toggle"), isOpen && getBlocksWith("__toggle--open")].concat(_toConsumableArray(spreadableProp$1(consumerClassName)))),
    "aria-describedby": "".concat(uniqueName, "-").concat(itemId, "-label"),
    "aria-label": isOpen ? toggleExpandedLabel : toggleCollapsedLabel,
    "aria-expanded": isOpen,
    "aria-controls": "".concat(uniqueName, "-").concat(index)
  }, consumerProps);
};

var _excluded$8 = ["isSubsection", "index", "className"],
    _excluded2$1 = ["hasSubsections", "childListIndex", "id", "className"],
    _excluded3 = ["id", "className", "dir", "locale", "onClick", "onKeyDown", "tabIndex"],
    _excluded4 = ["className"],
    _excluded5 = ["className", "id"],
    _excluded6 = ["className"],
    _excluded7 = ["className"],
    _excluded8 = ["index", "onClick", "onKeyDown", "className", "id"];

function ownKeys$9(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$9(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$9(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$9(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var BASE_CLASS = "eb-section-nav";

var spreadableProp = function spreadableProp(prop) {
  return Array.isArray(prop) ? prop : [prop];
};

var useList = function useList() {
  var initialList = [];
  var registeredLists = useRef(initialList);
  var registerList = useCallback(function (props) {
    var listIsRegistered = registeredLists.current.find(function (_ref) {
      var currentIndex = _ref.index;
      return currentIndex === props.index;
    });

    if (!listIsRegistered) {
      registeredLists.current = [].concat(_toConsumableArray(registeredLists.current), [props]);
    }
  }, []);
  return [registeredLists, registerList];
};

var useOpenLists = function useOpenLists() {
  var initialOpenLists = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];

  var _useState = useState(initialOpenLists),
      _useState2 = _slicedToArray(_useState, 2),
      openList = _useState2[0],
      setOpenList = _useState2[1];

  var toggleOpenList = useCallback(function (index) {
    var openIndex = openList.indexOf(index);

    if (openIndex === -1) {
      setOpenList([].concat(_toConsumableArray(openList), [index]));
      return;
    }

    setOpenList([].concat(_toConsumableArray(openList.slice(0, openIndex)), _toConsumableArray(openList.slice(openIndex + 1))));
  }, [openList]);
  return [openList, toggleOpenList];
};

var useSectionNavigationUtils = function useSectionNavigationUtils() {
  var controlledProps = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var toggleExpandedLabel = controlledProps.toggleExpandedLabel,
      toggleCollapsedLabel = controlledProps.toggleCollapsedLabel,
      newCurrentItemId = controlledProps.currentItemId,
      isFlat = controlledProps.isFlat,
      consumerUniqueName = controlledProps.uniqueName,
      consumerBlockClassName = controlledProps.consumerBlockClassName;

  var _useState3 = useState(consumerUniqueName),
      _useState4 = _slicedToArray(_useState3, 2),
      uniqueName = _useState4[0],
      setUniqueName = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isAutogenerated = _useState6[0],
      setAutogenerated = _useState6[1];

  var _useList = useList(),
      _useList2 = _slicedToArray(_useList, 2),
      registeredLists = _useList2[0],
      registerList = _useList2[1];

  var _useOpenLists = useOpenLists(),
      _useOpenLists2 = _slicedToArray(_useOpenLists, 2),
      openedLists = _useOpenLists2[0],
      toggleOpenList = _useOpenLists2[1];

  var _useState7 = useState(isFlat ? null : newCurrentItemId || null),
      _useState8 = _slicedToArray(_useState7, 2),
      currentItemId = _useState8[0],
      setCurrentItemId = _useState8[1];

  var getBlocksWith = useCallback(function (props) {
    return createBemBlockBuilder([BASE_CLASS].concat(_toConsumableArray(spreadableProp(consumerBlockClassName))))(props);
  }, [consumerBlockClassName]);
  useEffect(function () {
    if (isFlat) {
      setCurrentItemId(null);
      return;
    }

    if (!isFlat && newCurrentItemId && currentItemId !== newCurrentItemId) {
      setCurrentItemId(newCurrentItemId);
    }
  }, [currentItemId, newCurrentItemId, isFlat]);
  useEffect(function () {
    // If consumer unique name is not blank and unique name is autogenerated, set it
    if (isAutogenerated && consumerUniqueName) {
      setUniqueName(consumerUniqueName);
      setAutogenerated(false);
    } // If unique name is not autogenerated and unique name does not equal consumer unique name, set it


    if (!isAutogenerated && consumerUniqueName !== uniqueName) {
      setUniqueName(consumerUniqueName);
      setAutogenerated(false);
    } // If unique name is blank and not autogenerated, randomize it


    if (!isAutogenerated && !uniqueName) {
      setUniqueName("eb-".concat(String(Math.random()).slice(2, 7)));
      setAutogenerated(true);
    }
  }, [consumerUniqueName, uniqueName, isAutogenerated]);
  /**
   * refCallback
   * Provides a way to store a node with its current index.
   *
   * @param {number} index
   */

  var refCallback = useCallback(function (index) {
    var ref = {
      current: null
    };
    return function (node) {
      if (node) {
        ref.current = node;
        registerList({
          index: index,
          ref: ref
        });
      }
    };
  }, [registerList]);
  /**
   * getListProps
   *
   * @param {boolean} isSubsection
   * @param {boolean} isFlat
   * @param {number} index
   * @param {string|string[]} className
   * @param {object} consumerProps
   *
   * @returns Expected props for the list.
   */

  var getListProps = useCallback(function (_ref2) {
    var isSubsection = _ref2.isSubsection,
        index = _ref2.index,
        consumerClassName = _ref2.className,
        consumerProps = _objectWithoutProperties(_ref2, _excluded$8);

    var isOpen = openedLists.indexOf(index) !== -1;
    return _objectSpread$9({
      className: cn.apply(void 0, [getBlocksWith("__list"), isSubsection && getBlocksWith("__list--subsection"), (isOpen || isFlat) && getBlocksWith("__list--open"), isFlat && getBlocksWith("__list--flat")].concat(_toConsumableArray(spreadableProp(consumerClassName)))),
      ref: refCallback(index),
      id: isSubsection && "".concat(uniqueName, "-").concat(index) || undefined,
      role: "list"
    }, consumerProps);
  }, [openedLists, getBlocksWith, refCallback, uniqueName, isFlat]);
  /**
   * getListItemProps
   *
   * @param {string} id
   * @param {string|string[]} className
   * @param {string} childListIndex
   * @param {boolean} hasSubsections
   * @param {object} consumerProps
   *
   * @returns Expected props for the list item.
   */

  var getListItemProps = useCallback(function (_ref3) {
    var hasSubsections = _ref3.hasSubsections,
        childListIndex = _ref3.childListIndex,
        id = _ref3.id,
        consumerClassName = _ref3.className,
        consumerProps = _objectWithoutProperties(_ref3, _excluded2$1);

    var isCurrent = !isFlat && id === currentItemId;
    var isOpen = openedLists.indexOf(childListIndex) !== -1;
    return _objectSpread$9({
      className: cn.apply(void 0, [getBlocksWith("__list-item"), hasSubsections && getBlocksWith("__list-item--has-subsections"), isOpen && getBlocksWith("__list-item--open"), isCurrent && getBlocksWith("__list-item--current")].concat(_toConsumableArray(spreadableProp(consumerClassName))))
    }, consumerProps);
  }, [getBlocksWith, isFlat, currentItemId, openedLists]);
  /**
   * getListItemLabelProps
   *
   * @param {string} id
   * @param {string|string[]} className
   * @param {string} dir
   * @param {string} locale
   * @param {function} onClick
   * @param {function} onKeyDown
   * @param {string} tabIndex
   * @param {object} consumerProps
   *
   * @returns {object} Expected props for the list item label.
   */

  var getListItemLabelProps = useCallback(function (_ref4) {
    var itemId = _ref4.id,
        consumerClassName = _ref4.className;
        _ref4.dir;
        var locale = _ref4.locale,
        _ref4$onClick = _ref4.onClick,
        consumerOnClick = _ref4$onClick === void 0 ? function () {} : _ref4$onClick,
        _ref4$onKeyDown = _ref4.onKeyDown,
        consumerOnKeyDown = _ref4$onKeyDown === void 0 ? function () {} : _ref4$onKeyDown;
        _ref4.tabIndex;
        var consumerProps = _objectWithoutProperties(_ref4, _excluded3);

    var isCurrent = !isFlat && itemId === currentItemId;
    return _objectSpread$9(_objectSpread$9({
      className: cn.apply(void 0, [getBlocksWith("__list-item-label")].concat(_toConsumableArray(spreadableProp(consumerClassName)))),
      dir: "auto",
      id: "".concat(uniqueName, "-").concat(itemId, "-label"),
      lang: locale,
      onClick: function onClick(event) {
        !isFlat && setCurrentItemId(itemId);
        consumerOnClick({
          event: event,
          current: itemId
        });
      },
      onKeyDown: function onKeyDown(event) {
        var key = event.key;

        switch (key) {
          case " ":
          case "Spacebar":
          case "Enter":
            !isFlat && setCurrentItemId(itemId);
        }

        consumerOnKeyDown({
          event: event,
          current: itemId
        });
      },
      tabIndex: 0
    }, isCurrent ? {
      "aria-current": "true"
    } : {}), consumerProps);
  }, [currentItemId, getBlocksWith, isFlat, uniqueName]);
  /**
   * getAuxiliaryWrapperProps
   *
   * @param {string|string[]} className
   * @param {object} consumerProps
   *
   * @returns Expected props for the AuxiliaryWrapper.
   */

  var getAuxiliaryWrapperProps = useCallback(function (_ref5) {
    var consumerClassName = _ref5.className,
        consumerProps = _objectWithoutProperties(_ref5, _excluded4);

    return _objectSpread$9({
      className: cn.apply(void 0, [getBlocksWith("__auxiliary-wrapper")].concat(_toConsumableArray(spreadableProp(consumerClassName))))
    }, consumerProps);
  }, [getBlocksWith]);
  /**
   * getAuxiliaryIconProps
   *
   * @param {string} id
   * @param {string|string[]} className
   * @param {object} consumerProps
   *
   * @returns {object} Expected props for the AuxiliaryIcon.
   */

  var getAuxiliaryIconProps = useCallback(function (_ref6) {
    var consumerClassName = _ref6.className,
        itemId = _ref6.id,
        consumerProps = _objectWithoutProperties(_ref6, _excluded5);

    return _objectSpread$9({
      className: cn.apply(void 0, [getBlocksWith("__auxiliary-icon")].concat(_toConsumableArray(spreadableProp(consumerClassName)))),
      "aria-describedby": "".concat(uniqueName, "-").concat(itemId, "-label")
    }, consumerProps);
  }, [getBlocksWith, uniqueName]);
  /**
   * getNavWrapperProps
   *
   * @param {string|string[]} className
   * @param {object} consumerProps
   *
   * @returns {object} Expected props for the NavWrapper.
   */

  var getNavWrapperProps = useCallback(function (_ref7) {
    var consumerClassName = _ref7.className,
        consumerProps = _objectWithoutProperties(_ref7, _excluded6);

    return _objectSpread$9({
      "aria-labelledby": "".concat(uniqueName, "-header"),
      className: cn.apply(void 0, [getBlocksWith("__nav-wrapper"), isFlat && getBlocksWith("__nav-wrapper--flat")].concat(_toConsumableArray(spreadableProp(consumerClassName))))
    }, consumerProps);
  }, [getBlocksWith, isFlat, uniqueName]);
  var getHeaderProps = useCallback(function (_ref8) {
    var consumerClassName = _ref8.className,
        consumerProps = _objectWithoutProperties(_ref8, _excluded7);

    return _objectSpread$9({
      id: "".concat(uniqueName, "-header"),
      className: cn.apply(void 0, [getBlocksWith("__header")].concat(_toConsumableArray(spreadableProp(consumerClassName)))),
      dir: "auto"
    }, consumerProps);
  }, [getBlocksWith, uniqueName]);
  /**
   * getToggleProps
   *
   * @param {string} id
   * @param {number | string} index
   * @param {Function} onClick
   * @param {Function} onKeyDown
   * @param {string | string[]} className
   */

  var getToggleProps = useCallback(function (_ref9) {
    var index = _ref9.index,
        onClick = _ref9.onClick,
        onKeyDown = _ref9.onKeyDown,
        className = _ref9.className,
        id = _ref9.id,
        consumerProps = _objectWithoutProperties(_ref9, _excluded8);

    return buildGetToggleProps(_objectSpread$9({
      registeredLists: registeredLists,
      openedLists: openedLists,
      getBlocksWith: getBlocksWith,
      toggleExpandedLabel: toggleExpandedLabel,
      toggleCollapsedLabel: toggleCollapsedLabel,
      uniqueName: uniqueName,
      toggleOpenList: toggleOpenList,
      index: index,
      id: id,
      onClick: onClick,
      onKeyDown: onKeyDown,
      className: className
    }, consumerProps));
  }, [registeredLists, openedLists, getBlocksWith, toggleExpandedLabel, toggleCollapsedLabel, uniqueName, toggleOpenList]);
  return useMemo(function () {
    return {
      getAuxiliaryIconProps: getAuxiliaryIconProps,
      getAuxiliaryWrapperProps: getAuxiliaryWrapperProps,
      getListProps: getListProps,
      getListItemProps: getListItemProps,
      getNavWrapperProps: getNavWrapperProps,
      getHeaderProps: getHeaderProps,
      getToggleProps: getToggleProps,
      getListItemLabelProps: getListItemLabelProps,
      getBlocksWith: getBlocksWith,
      openedLists: openedLists,
      toggleOpenList: toggleOpenList,
      registeredLists: registeredLists,
      registerList: registerList,
      setCurrentItemId: setCurrentItemId,
      currentItemId: currentItemId,
      isFlat: isFlat,
      uniqueName: uniqueName
    };
  }, [currentItemId, getAuxiliaryIconProps, getAuxiliaryWrapperProps, getBlocksWith, getHeaderProps, getListItemLabelProps, getListItemProps, getListProps, getNavWrapperProps, getToggleProps, isFlat, openedLists, registerList, registeredLists, toggleOpenList, uniqueName]);
};

var _excluded$7 = ["children", "className"];

function ownKeys$8(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$8(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$8(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$8(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var SectionNavigationNavWrapper = function SectionNavigationNavWrapper(_ref) {
  var children = _ref.children,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? "" : _ref$className,
      rest = _objectWithoutProperties(_ref, _excluded$7);

  var _useSectionNavigation = useSectionNavigationContext(),
      getNavWrapperProps = _useSectionNavigation.getNavWrapperProps;

  if (!getNavWrapperProps) {
    throw new Error("SectionNavigation.NavWrapper component requires getNavWrapperProps to be defined");
  }

  return /*#__PURE__*/jsx("nav", _objectSpread$8(_objectSpread$8({}, getNavWrapperProps(_objectSpread$8({
    className: className
  }, rest))), {}, {
    children: children
  }));
};

var _excluded$6 = ["children", "className", "customHTMLTag"];

function ownKeys$7(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$7(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$7(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$7(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var SectionNavigationHeader = function SectionNavigationHeader(_ref) {
  var children = _ref.children;
      _ref.className;
      var _ref$customHTMLTag = _ref.customHTMLTag,
      Wrapper = _ref$customHTMLTag === void 0 ? "h2" : _ref$customHTMLTag,
      rest = _objectWithoutProperties(_ref, _excluded$6);

  var _useSectionNavigation = useSectionNavigationContext(),
      getHeaderProps = _useSectionNavigation.getHeaderProps;

  if (!getHeaderProps) {
    throw new Error("SectionNavigation.Header component requires getHeaderProps to be defined");
  }

  return /*#__PURE__*/jsx(Wrapper, _objectSpread$7(_objectSpread$7({}, getHeaderProps(rest)), {}, {
    children: children
  }));
};

var _excluded$5 = ["index", "isSubsection", "children"];

function ownKeys$6(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$6(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$6(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$6(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var SectionNavigationList = function SectionNavigationList(_ref) {
  var index = _ref.index,
      _ref$isSubsection = _ref.isSubsection,
      isSubsection = _ref$isSubsection === void 0 ? false : _ref$isSubsection,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$5);

  var _useSectionNavigation = useSectionNavigationContext(),
      getListProps = _useSectionNavigation.getListProps;

  if (!getListProps) {
    throw new Error("SectionNavigation.List component requires getListProps to be defined");
  }

  return /*#__PURE__*/jsx("ul", _objectSpread$6(_objectSpread$6({}, getListProps(_objectSpread$6({
    isSubsection: isSubsection,
    index: index
  }, rest))), {}, {
    children: children
  }));
};

var SectionNavigationList$1 = memo(SectionNavigationList);

var _excluded$4 = ["children", "hasSubsections", "childListIndex", "id"];

function ownKeys$5(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$5(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$5(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$5(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var SectionNavigationListItem = function SectionNavigationListItem(_ref) {
  var children = _ref.children,
      _ref$hasSubsections = _ref.hasSubsections,
      hasSubsections = _ref$hasSubsections === void 0 ? true : _ref$hasSubsections,
      _ref$childListIndex = _ref.childListIndex,
      childListIndex = _ref$childListIndex === void 0 ? "" : _ref$childListIndex,
      id = _ref.id,
      rest = _objectWithoutProperties(_ref, _excluded$4);

  var _useSectionNavigation = useSectionNavigationContext(),
      getListItemProps = _useSectionNavigation.getListItemProps;

  if (!getListItemProps) {
    throw new Error("SectionNavigation.ListItem component requires getListItemProps to be defined");
  }

  return /*#__PURE__*/jsx("li", _objectSpread$5(_objectSpread$5({}, getListItemProps(_objectSpread$5({
    hasSubsections: hasSubsections,
    id: id,
    childListIndex: childListIndex
  }, rest))), {}, {
    children: children
  }));
};

var SectionNavigationListItem$1 = memo(SectionNavigationListItem);

var _excluded$3 = ["children"];

function ownKeys$4(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$4(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$4(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$4(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var SectionNavigationListItemContent = function SectionNavigationListItemContent(_ref) {
  var children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$3);

  var _useSectionNavigation = useSectionNavigationContext(),
      getBlocksWith = _useSectionNavigation.getBlocksWith;

  if (!getBlocksWith) {
    throw new Error("SectionNavigation component requires a class builder to be defined");
  }

  return /*#__PURE__*/jsx("div", _objectSpread$4(_objectSpread$4({
    className: getBlocksWith("__list-item-content")
  }, rest), {}, {
    children: children
  }));
};

var _excluded$2 = ["index", "className", "icon", "id"];

function ownKeys$3(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$3(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$3(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var SectionNavigationToggle = function SectionNavigationToggle(_ref) {
  var index = _ref.index,
      className = _ref.className,
      icon = _ref.icon,
      id = _ref.id,
      rest = _objectWithoutProperties(_ref, _excluded$2);

  var _useSectionNavigation = useSectionNavigationContext(),
      getToggleProps = _useSectionNavigation.getToggleProps,
      isFlat = _useSectionNavigation.isFlat;

  if (!getToggleProps) {
    throw new Error("SectionNavigation.Toggle component requires getToggleProps to be defined");
  }

  if (isFlat) {
    return null;
  }

  var toggleIcon = icon ? icon : faAngleDown;
  return /*#__PURE__*/jsx("button", _objectSpread$3(_objectSpread$3({}, getToggleProps(_objectSpread$3({
    index: index,
    id: id,
    className: className
  }, rest))), {}, {
    children: /*#__PURE__*/jsx(FontAwesomeIcon, {
      className: "eb-section-nav__toggle-icon",
      icon: toggleIcon
    })
  }));
};

var _excluded$1 = ["children"];

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var SectionNavigationAuxiliaryWrapper = function SectionNavigationAuxiliaryWrapper(_ref) {
  var children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var _useSectionNavigation = useSectionNavigationContext(),
      getAuxiliaryWrapperProps = _useSectionNavigation.getAuxiliaryWrapperProps;

  if (!getAuxiliaryWrapperProps) {
    throw new Error("SectionNavigation.AuxiliaryWrapper component requires getAuxiliaryWrapperProps to be defined");
  }

  return /*#__PURE__*/jsx("div", _objectSpread$2(_objectSpread$2({}, getAuxiliaryWrapperProps(_objectSpread$2({}, rest))), {}, {
    children: children
  }));
};

var destructureItem = function destructureItem(item, getters) {
  var getId = getters.getId,
      getLabel = getters.getLabel,
      getSubsections = getters.getSubsections;

  if (!("id" in item) && !getId) {
    throw new Error("SectionNavigationItemsMapper: getId prop is required!");
  }

  if (!("label" in item) && !getLabel) {
    throw new Error("SectionNavigationItemsMapper: getLabel prop is required!");
  }

  var maybeDefaultItem = item;
  return {
    id: maybeDefaultItem.id || getId && getId(item) || "",
    label: maybeDefaultItem.label || getLabel && getLabel(item) || "",
    subsections: maybeDefaultItem.subsections || getSubsections && getSubsections(item) || []
  };
};
var countMaxLevel = function countMaxLevel(items, getters) {
  var level = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  return items.reduce(function (acc, item) {
    var _destructureItem = destructureItem(item, getters),
        subsections = _destructureItem.subsections;

    if (!subsections) {
      return Math.max(acc, level);
    }

    return Math.max(acc, level, countMaxLevel(subsections, getters, level + 1));
  }, level);
};

var _excluded = ["item", "label", "subsections", "index"],
    _excluded2 = ["items", "index", "isSubsection", "ItemContent", "ListItem", "List", "getLabel", "getId", "getSubsections", "level", "renderLevel", "getters"];

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var DefaultListItem = memo(function (props) {
  props.item;
      props.label;
      props.subsections;
      props.index;
      var rest = _objectWithoutProperties(props, _excluded);

  return /*#__PURE__*/jsx(SectionNavigationListItem$1, _objectSpread$1({}, rest));
});

var ListBuilder = function ListBuilder(props) {
  var items = props.items,
      _props$index = props.index,
      parentIndex = _props$index === void 0 ? 1 : _props$index,
      _props$isSubsection = props.isSubsection,
      isSubsection = _props$isSubsection === void 0 ? false : _props$isSubsection,
      ItemContent = props.ItemContent,
      _props$ListItem = props.ListItem,
      ListItem = _props$ListItem === void 0 ? DefaultListItem : _props$ListItem,
      _props$List = props.List,
      List = _props$List === void 0 ? SectionNavigationList$1 : _props$List,
      getLabel = props.getLabel,
      getId = props.getId,
      getSubsections = props.getSubsections,
      _props$level = props.level,
      level = _props$level === void 0 ? 0 : _props$level,
      renderLevel = props.renderLevel,
      getters = props.getters,
      rest = _objectWithoutProperties(props, _excluded2);

  if (level > renderLevel) {
    return null;
  }

  return /*#__PURE__*/jsx(List, {
    index: parentIndex,
    isSubsection: isSubsection,
    children: items.map(function (item, itemIndex) {
      var _destructureItem = destructureItem(item, getters),
          id = _destructureItem.id,
          label = _destructureItem.label,
          subsections = _destructureItem.subsections;

      var hasSubsections = subsections.length > 0;
      return /*#__PURE__*/jsxs(ListItem, {
        childListIndex: "".concat(parentIndex, "-").concat(itemIndex + 1),
        index: "".concat(parentIndex, "-").concat(itemIndex + 1),
        hasSubsections: hasSubsections,
        id: id,
        label: label,
        subsections: subsections,
        item: item,
        children: [/*#__PURE__*/jsx(ItemContent, _objectSpread$1({
          id: id,
          label: label,
          index: "".concat(parentIndex, "-").concat(itemIndex + 1),
          subsections: subsections,
          item: item
        }, rest)), hasSubsections && /*#__PURE__*/jsx(ListBuilder, {
          items: subsections,
          index: "".concat(parentIndex, "-").concat(itemIndex + 1),
          ItemContent: ItemContent,
          List: List,
          ListItem: ListItem,
          isSubsection: true,
          getLabel: getLabel,
          getId: getId,
          getSubsections: getSubsections,
          level: level + 1,
          renderLevel: renderLevel,
          getters: getters
        })]
      }, id);
    })
  });
}; // React.memo breaks generics


var ListBuilder$1 = memo(ListBuilder);

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var SectionNavigationNavListBuilder = function SectionNavigationNavListBuilder(props) {
  var items = props.items,
      getLabel = props.getLabel,
      getId = props.getId,
      _props$getSubsections = props.getSubsections,
      getSubsections = _props$getSubsections === void 0 ? function (item) {
    return item.subsections;
  } : _props$getSubsections;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      renderLevel = _useState2[0],
      setRenderLevel = _useState2[1];

  var getters = useMemo(function () {
    return {
      getId: getId,
      getLabel: getLabel,
      getSubsections: getSubsections
    };
  }, [getId, getLabel, getSubsections]);
  var maxLevel = useMemo(function () {
    return countMaxLevel(items, getters);
  }, [getters, items]);
  useLayoutEffect(function () {
    if (renderLevel < maxLevel) {
      setRenderLevel(function (prevState) {
        return prevState + 1;
      });
    }
  }, [maxLevel, renderLevel]);
  return /*#__PURE__*/jsx(ListBuilder$1, _objectSpread(_objectSpread({}, props), {}, {
    renderLevel: renderLevel,
    getters: getters
  }));
};

var SectionNavigationNavListBuilder$1 = memo(SectionNavigationNavListBuilder);

var SectionNavigation = forwardRef(function SectionNavigation(_ref, forwardedRef) {
  var _ref$customHTMLTag = _ref.customHTMLTag,
      Wrapper = _ref$customHTMLTag === void 0 ? "div" : _ref$customHTMLTag,
      _ref$toggleExpandedLa = _ref.toggleExpandedLabel,
      toggleExpandedLabel = _ref$toggleExpandedLa === void 0 ? "Collapse" : _ref$toggleExpandedLa,
      _ref$toggleCollapsedL = _ref.toggleCollapsedLabel,
      toggleCollapsedLabel = _ref$toggleCollapsedL === void 0 ? "Expand" : _ref$toggleCollapsedL,
      currentItemId = _ref.currentItemId,
      isFlat = _ref.isFlat,
      consumerBlockClassName = _ref.className,
      uniqueName = _ref.uniqueName,
      children = _ref.children;
  var controlledProps = useMemo(function () {
    return {
      toggleExpandedLabel: toggleExpandedLabel,
      toggleCollapsedLabel: toggleCollapsedLabel,
      currentItemId: currentItemId,
      isFlat: isFlat,
      consumerBlockClassName: consumerBlockClassName,
      uniqueName: uniqueName
    };
  }, [consumerBlockClassName, currentItemId, isFlat, toggleCollapsedLabel, toggleExpandedLabel, uniqueName]);
  var utils = useSectionNavigationUtils(controlledProps);
  return /*#__PURE__*/jsx(SectionNavigationContext.Provider, {
    value: utils,
    children: /*#__PURE__*/jsx(Wrapper, {
      ref: forwardedRef,
      className: utils.getBlocksWith(),
      children: children
    })
  });
});
SectionNavigationContextComponent.displayName = "SectionNavigation.Context";
SectionNavigation.Context = SectionNavigationContextComponent;
SectionNavigationHeader.displayName = "SectionNavigation.Header";
SectionNavigation.Header = SectionNavigationHeader;
SectionNavigationToggle.displayName = "SectionNavigation.Toggle";
SectionNavigation.Toggle = SectionNavigationToggle;
SectionNavigationNavWrapper.displayName = "SectionNavigation.NavWrapper";
SectionNavigation.NavWrapper = SectionNavigationNavWrapper;
SectionNavigationAuxiliaryWrapper.displayName = "SectionNavigation.AuxiliaryWrapper";
SectionNavigation.AuxiliaryWrapper = SectionNavigationAuxiliaryWrapper;
SectionNavigationList$1.displayName = "SectionNavigation.List";
SectionNavigation.List = SectionNavigationList$1;
SectionNavigationListItem$1.displayName = "SectionNavigation.ListItem";
SectionNavigation.ListItem = SectionNavigationListItem$1;
SectionNavigationListItemContent.displayName = "SectionNavigation.ListItemContent";
SectionNavigation.ListItemContent = SectionNavigationListItemContent;
SectionNavigationNavListBuilder$1.displayName = "SectionNavigation.NavListBuilder";
SectionNavigation.NavListBuilder = SectionNavigationNavListBuilder$1;
SectionNavigation.useSectionNavigationContext = useSectionNavigationContext;

export { SectionNavigation as default };
//# sourceMappingURL=section-navigation-esm.js.map
